/**
 * Canonical envelope document row for CreateEnvelope Step 1.
 * Single source of truth: stable row identity, file payload, server document id.
 */

export function newEnvelopeRowId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `row_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
}

export function normalizeRowServerDocumentId(raw) {
  if (raw === undefined || raw === null || raw === '' || raw === 0) return null;
  const n = typeof raw === 'string' ? parseInt(raw, 10) : raw;
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Remove backend ids mutably attached to a local File after upload (replace must start clean). */
export function clearBackendDocumentIdStamp(file) {
  if (!file || typeof file !== 'object' || file.fromApi) return;
  try {
    // eslint-disable-next-line no-param-reassign
    delete file.documentId;
    // eslint-disable-next-line no-param-reassign
    delete file.document_id;
    // eslint-disable-next-line no-param-reassign
    delete file.original_document_id;
  } catch {
    /* ignore */
  }
}

/**
 * @param {File|object} file
 * @param {number|null|undefined} serverDocumentId — explicit server id; for fromApi may be omitted and inferred from file
 * @param {{ rowId?: string }} [options]
 * @returns {{ rowId: string, file: File|object, serverDocumentId: number|null }}
 */
export function createEnvelopeDocumentRow(file, serverDocumentId = null, options = {}) {
  const rowId = options.rowId || newEnvelopeRowId();
  let sid = normalizeRowServerDocumentId(serverDocumentId);
  if (sid == null && file && file.fromApi) {
    sid = normalizeRowServerDocumentId(file.documentId ?? file.document_id ?? file.original_document_id);
  }
  return { rowId, file, serverDocumentId: sid };
}

export function mapFilesAndIdsToEnvelopeRows(files, ids) {
  const list = Array.isArray(files) ? files : [];
  return list.map((file, i) => createEnvelopeDocumentRow(file, ids?.[i] ?? null));
}

export function reorderEnvelopeDocumentRows(rows, fromIndex, toIndex) {
  if (!Array.isArray(rows) || fromIndex === toIndex) return rows ? [...rows] : [];
  const out = [...rows];
  if (
    fromIndex < 0
    || toIndex < 0
    || fromIndex >= out.length
    || toIndex >= out.length
  ) {
    return out;
  }
  const [moved] = out.splice(fromIndex, 1);
  out.splice(toIndex, 0, moved);
  return out;
}
